import React from "react";
import PrimaryButton from "components/common/buttons/PrimaryButton";
import ConsentPreferenceList from "components/common/consent/ConsentPreferenceList";
import consentShield from "assests/images/consent-shield.svg";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { useLocale } from "lib/hooks/useLocale";
import { Consent, ConsentAnswers } from "lib/types/consent";

// Radial mint wash behind the header, from the design.
const HEADER_BACKGROUND =
  "radial-gradient(ellipse 100% 100% at 50% 0%, rgba(0,212,146,0.2) 0%, rgba(111,223,201,0.1) 50%, rgba(221,233,255,0) 100%)";

interface ConsentPreferencesContentProps {
  consents: Consent[];
  answers: ConsentAnswers;
  onChange: (code: string, isAccepted: boolean) => void;
  onReadMore: (consent: Consent) => void;
  onSave: () => void;
  /** Top-left control, e.g. a back arrow in the SSO popup */
  headerAction?: React.ReactNode;
  /** Height cap; the list scrolls while Save Preferences stays pinned at the bottom */
  maxHeightClassName?: string;
}

const ConsentPreferencesContent: React.FC<ConsentPreferencesContentProps> = ({
  consents,
  answers,
  onChange,
  onReadMore,
  onSave,
  headerAction,
  maxHeightClassName = "max-h-[90vh]",
}) => {
  const { t } = useLocale();
  const {
    state: { merchant },
  } = useMerchantContext();

  const storeOrigin = merchant?.domain
    ? merchant.domain.startsWith("http")
      ? merchant.domain
      : `https://${merchant.domain}`
    : "";

  const openPolicy = (path: string) => {
    if (!storeOrigin) return;
    window.open(`${storeOrigin}${path}`, "_blank", "noopener,noreferrer");
  };

  return (
    <div className={`flex w-full flex-col overflow-hidden rounded-t-3xl bg-white ${maxHeightClassName}`}>
      <div className="scrollbar-hide min-h-0 flex-1 overflow-y-auto">
        <div className="relative w-full overflow-hidden" style={{ background: HEADER_BACKGROUND }}>
          {headerAction && <div className="px-6 pt-4">{headerAction}</div>}
          <h2 className="w-[235px] px-6 pb-6 pt-8 text-xl font-semibold leading-[30px] tracking-[-0.2px] text-[#4d4d4d]">
            {t("consent_preferences_title")}
          </h2>
          <img
            src={consentShield}
            alt=""
            width={106}
            height={131}
            className="pointer-events-none absolute -top-[18px] right-[3px]"
          />
        </div>

        <div className="pt-7">
          <ConsentPreferenceList
            consents={consents}
            answers={answers}
            onChange={onChange}
            onReadMore={onReadMore}
          />
        </div>
      </div>

      <div className="flex w-full shrink-0 flex-col gap-[5px] pb-4 pt-6">
        <div className="w-full px-4 py-2">
          <PrimaryButton
            type="button"
            buttonText={t("consent_save_preferences")}
            height="h-[55px]"
            borderRadius="rounded-2xl"
            isCheckout={false}
            onClick={onSave}
          />
        </div>
        <div className="flex w-full items-center justify-center gap-2 px-6 py-1 text-[10px] leading-4 tracking-[-0.1px] text-[#4d4d4d]/70">
          <button type="button" className="underline" onClick={() => openPolicy("/policies/privacy-policy")}>
            {t("privacy_policy")}
          </button>
          <button
            type="button"
            className="underline"
            onClick={() => openPolicy("/policies/terms-of-service")}>
            {t("profile_terms_of_use")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConsentPreferencesContent;
